import React, { AnimationEvent, useEffect, useState, PropsWithChildren, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { ControlsStringAssets, KeyValuePairsStringAssets, MessagesStringAssets } from '../../../assets/stringAssets';
import { IRegistrationData } from '../../../dataObjects/models/registerAndLogin/RegistrationData';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, BoxProps, Button, ButtonProps, Checkbox, FormControlLabel, LinearProgress, LinearProgressProps, MenuItem, TextField, TextFieldProps, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import SubmitButton from '../../controls/buttons/SubmitButton/SubmitButton';
import Form from '../Form/Form';
import { normalizePhoneNumber } from '../../utilities';
import isEmail from 'validator/lib/isEmail';
import { enumLocale, enumSubscriptionType, enumSubscriptionTypeConvert, enumTheme } from '../../../dataObjects/enums';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/styles';
import { RegularExpressions } from '../../../assets/regularExpressionAssets';
import { getUserRedemptionCodeRequestsForEmailAddressViaHttp } from '../../../firebaseServices/dataServices/dataServiceActions/userRedemptionCodeRequestActions';
import { IUserRedemptionCodeRequest, IUserRedemptionCodeRequestAsJson } from '../../../dataObjects/models/userRedemptionCodeRequest';


/*** Using the Material UI Emotion Styling library, declare 'styled' instances for each area/object. 
 *** NOTE: These must be declared outside of the React Functional Component to ensure that the styled 
 *** objects will be properly rendered within the DOM. 
 ***/

// a styled Box (equivalent to a <div>), providing an area to display the overall Progress Bar Area
const StyledBoxForProgressBarArea = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  marginTop: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  width: "100%",
}));

// a styled LinearProgress for the Progress Bar
const StyledLinearProgressForProgressBar = styled((props: LinearProgressProps) => (
  <LinearProgress
    {...props}
  />
))(({ theme }) => ({
  width: "100%",
}));

// a styled Box (equivalent to a <div>), providing an area to display the Progress Label Area
const StyledBoxForProgressLabelArea = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  width: "100%",
}));

// a styled Box (equivalent to a <div>), providing an area to display the Action Buttons Area
const StyledBoxForActionButtonsArea = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  marginTop: theme.spacing(2),
  // flexDirection: 'column',
}));

// a styled Box (equivalent to a <div>), providing an area to display a Pseudo Horizontal Rule
const StyledBoxForPseudoHorizontalRule = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
  textAlign: 'center',
  borderBottom: `1px solid ${theme.palette.primary.main}`,
  lineHeight: '0.1em',
  margin: '20px 0px',
}));

// a styled Box (equivalent to a <div>), providing an area to display Text within the Pseudo Horizontal Rule
const StyledBoxForTextInPseudoHorizontalRule = styled((props: BoxProps) => (
  <Box
    component='span'
    {...props}
  />
))(({ theme }) => ({
  background: '#fff',
  padding: '0 10px',
}));

// a styled Button for the Register Button
const StyledButtonForLoginButton = styled((props: ButtonProps) => (
  <Button
    fullWidth
    {...props}
  />
))(({ theme }) => ({
  padding: theme.spacing(1, 2),
  color: theme.secondaryButton.textColor,
  border: theme.secondaryButton.border,
  background: theme.secondaryButton.background,
  '&:hover': {
    border: theme.secondaryButton.hoverBorder,
    background: theme.secondaryButton.hoverBackground,
    color: theme.secondaryButton.hoverTextColor,
  },
  '&:disabled': {
    border: theme.secondaryButton.disabledBorder,
    background: theme.secondaryButton.disabledBackground,
    color: theme.secondaryButton.disabledTextColor,
  },
}));

// a styled TextField that is serving as a Select control
const StyledTextFieldForSelectControl = styled((props: TextFieldProps) => (
  <TextField
    select
    fullWidth
    {...props}
  />
))(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
}));


interface IRegistrationFormValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  mobilePhone: string;
  theme: string;
  preferredLocale: string;
  membershipType: string;
  registrationCode: string;
}

const schema = yup.object().shape({
  email: yup
    .string()
    .email(ControlsStringAssets.emailInvalid)
    .required(ControlsStringAssets.emailRequired)
    .when('autofilled', ([autofilled], schema) => {
      return autofilled ? schema.notRequired() : schema.required('Required');
    }),
  password: yup
    .string()
    .min(8, ControlsStringAssets.passwordMinLength)
    .matches(RegularExpressions.Password, ControlsStringAssets.passwordPattern)
    .required(ControlsStringAssets.passwordRequired)
    .when('autofilled', ([autofilled], schema) => {
      return autofilled ? schema.notRequired() : schema.required('Required');
    }),
  confirmPassword: yup
    .string()
    .required(ControlsStringAssets.confirmPasswordRequired)
    .min(8, ControlsStringAssets.passwordMinLength)
    .oneOf([yup.ref('password')], ControlsStringAssets.confirmPasswordMustMatch) // validates that the confirmation password matches the original password
    .matches(RegularExpressions.Password, ControlsStringAssets.passwordPattern)
    .when('autofilled', ([autofilled], schema) => {
      return autofilled ? schema.notRequired() : schema.required('Required');
    }),
  firstName: yup
    .string()
    .required(ControlsStringAssets.firstNameRequired)
    .when('autofilled', ([autofilled], schema) => {
      return autofilled ? schema.notRequired() : schema.required('Required');
    }),
  lastName: yup
    .string()
    .required(ControlsStringAssets.lastNameRequired)
    .when('autofilled', ([autofilled], schema) => {
      return autofilled ? schema.notRequired() : schema.required('Required');
    }),
  mobilePhone: yup
    .string()
    .matches(RegularExpressions.PhoneNumber, ControlsStringAssets.phoneNumberInvalid)
    .required(ControlsStringAssets.phoneNumberRequired)
    .when('autofilled', ([autofilled], schema) => {
      return autofilled ? schema.notRequired() : schema.required('Required');
    }),
  theme: yup
    .string()
    .required(),
  preferredLocale: yup
    .string()
    .required(),
  membershipType: yup
    .string()
    .required(ControlsStringAssets.membershipTypeRequired),
  registrationCode: yup
    .string()
    .required(ControlsStringAssets.registrationCodeRequired)
    .when('autofilled', ([autofilled], schema) => {
      return autofilled ? schema.notRequired() : schema.required('Required');
    }),
});

export interface IRegistrationFormProps extends PropsWithChildren<unknown> {
  /**
   * @property {IRegistrationData} registrationData The RegistrationData details for the form (in production, the fields will be blank; in development, default info may be provided)
   */
  registrationData: IRegistrationData,
  /**
  * @property {boolean} registrationRequestInProgress Whether a registration request is in progress
  */
  registrationRequestInProgress?: boolean,
  /**
   * @property {(registrationData: IRegistrationData) => Promise<void>} onSubmit Method to call for submitting the form for a registration operation
   */
  onSubmit: (registrationData: IRegistrationData) => Promise<void>,
}

const RegistrationForm: React.FC<IRegistrationFormProps> = (props: IRegistrationFormProps) => {
  RegistrationForm.displayName = 'Register Form';

  // whether to display console logs (displayConsoleLogs && console.log statements)
  const displayConsoleLogs: boolean = false;

  const { registrationData, onSubmit } = props;

  const navigate = useNavigate();

  // set up details for ReactHookForm
  const { register, formState, formState: { errors }, getValues, handleSubmit, setError, setValue, clearErrors } = useForm<IRegistrationFormValues>({
    defaultValues: {
      email: registrationData.email ?? '',
      password: registrationData.password ?? '',
      confirmPassword: registrationData.password ?? '',
      firstName: registrationData.firstName ?? '',
      lastName: registrationData.lastName ?? '',
      mobilePhone: registrationData.mobilePhone ?? '',
      theme: (registrationData.theme !== undefined && registrationData.theme !== '') ? registrationData.theme : enumTheme.Default,
      preferredLocale: (registrationData.preferredLocale !== undefined && registrationData.preferredLocale !== '') ? registrationData.preferredLocale : enumLocale.English,
      membershipType: registrationData.subscriptionType ?? '', // enumSubscriptionType.BetaTesting,
      registrationCode: registrationData.registrationCode ?? '',
    },
    // mode: "onBlur",
    mode: "all",
    resolver: yupResolver(schema)
  });

  const { ref: emailReg, ...emailProps } = register("email", { required: true });
  const { ref: passwordReg, ...passwordProps } = register("password", { required: true });
  const { ref: confirmPasswordReg, ...confirmPasswordProps } = register("confirmPassword", { required: true });
  const { ref: firstNameReg, ...firstNameProps } = register("firstName", { required: true });
  const { ref: lastNameReg, ...lastNameProps } = register("lastName", { required: true });
  const { ref: mobilePhoneReg, ...mobilePhoneProps } = register("mobilePhone", { required: true });
  const { ref: themeReg } = register("theme", { required: true });
  const { ref: preferredLocaleReg } = register("preferredLocale", { required: true });
  const { ref: membershipTypeReg, ...membershipTypeProps } = register("membershipType", { required: true });
  const { ref: registrationCodeReg, ...registrationCodeProps } = register("registrationCode", { required: true });

  const [agreeToTerms, setAgreeToTerms] = useState<boolean>(false);

  // for testing whether the form is in a valid state
  const { isValid } = formState;

  const [formIsValid, setFormIsValid] = useState(false);

  // capture the current value of relevant form fields
  const [currentEmailAddress, setCurrentEmailAddress] = useState<string>('');
  const [currentMobilePhone, setCurrentMobilePhone] = useState<string>('');
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [currentConfirmPassword, setCurrentConfirmPassword] = useState<string>('');
  const [currentFirstName, setCurrentFirstName] = useState<string>('');
  const [currentLastName, setCurrentLastName] = useState<string>('');

  const [themesForDropdown, setThemesForDropdown] = useState<Array<React.JSX.Element>>([]);

  const [themeKey, setThemeKey] = useState<string>("");

  const [localesForDropdown, setLocalesForDropdown] = useState<Array<React.JSX.Element>>([]);

  const [preferredLocaleKey, setPreferredLocaleKey] = useState<string>("");

  // currently selected membershipType (membershipTypeKey is the selected item's value)
  // const [membershipTypeKey, setSubscriptionTypeKey] = useState<string>(enumSubscriptionTypeConvert.toString(enumSubscriptionType.BetaTesting));
  const [membershipTypeKey, setSubscriptionTypeKey] = useState<string>('');

  const [membershipTypesForDropdown, setMembershipTypesForDropdown] = useState<Array<React.JSX.Element>>([]);

  const [registrationCodes, setRegistrationCodes] = useState<Array<string>>([]);

  const [userRedemptionCodeRequests, setUserRedemptionCodeRequests] = useState<Array<IUserRedemptionCodeRequest>>([]);

  const [registrationCodeFieldValue, setRegistrationCodeFieldValue] = useState<string>("");

  const [registrationCodeIsValid, setRegistrationCodeIsValid] = useState<boolean>(false);

  const [isEmailAddressAutoFilled, setIsEmailAddressAutoFilled] = useState<boolean>(false);
  const [isMobilePhoneAutoFilled, setIsMobilePhoneAutoFilled] = useState<boolean>(false);
  const [isFirstNameAutoFilled, setIsFirstNameAutoFilled] = useState<boolean>(false);
  const [isLastNameAutoFilled, setIsLastNameAutoFilled] = useState<boolean>(false);
  const [isPasswordAutoFilled, setIsPasswordAutoFilled] = useState<boolean>(false);
  const [isConfirmPasswordAutoFilled, setIsConfirmPasswordAutoFilled] = useState<boolean>(false);

  // determines whether the email address field value is valid
  const validEmailAddressFieldValue: () => boolean = useCallback(
    () => {
      let isFieldValueValid: boolean = false;

      // see if the explicit value in the field (via a backing state variable) is valid
      if (currentEmailAddress !== undefined) {
        isFieldValueValid = RegularExpressions.EmailAddress.test(currentEmailAddress);
      }

      // if the explicit value in the field was not found to be valid, see if the value was autofilled (which, by definition,
      // would mean that the value was used before and, thus, had a valid pattern)
      if (!isFieldValueValid) {
        isFieldValueValid = isEmailAddressAutoFilled;
      }

      return isFieldValueValid;
    },
    [currentEmailAddress, isEmailAddressAutoFilled]
  );

  // determines whether the mobile phone field value is valid
  const validMobilePhoneFieldValue: () => boolean = useCallback(
    () => {
      let isFieldValueValid: boolean = false;

      // see if the explicit value in the field (via a backing state variable) is valid
      if (currentMobilePhone !== undefined) {
        isFieldValueValid = RegularExpressions.PhoneNumber.test(currentMobilePhone);
      }

      // if the explicit value in the field was not found to be valid, see if the value was autofilled (which, by definition,
      // would mean that the value was used before and, thus, had a valid pattern)
      if (!isFieldValueValid) {
        isFieldValueValid = isMobilePhoneAutoFilled;
      }

      return isFieldValueValid;
    },
    [currentMobilePhone, isMobilePhoneAutoFilled]
  );

  // determines whether the first name field value is valid
  const validFirstNameFieldValue: () => boolean = useCallback(
    () => {
      let isFieldValueValid: boolean = false;

      // see if the explicit value in the field (via a backing state variable) is valid
      if (currentFirstName !== undefined) {
        isFieldValueValid = currentFirstName.trim().length > 0;
      }

      // if the explicit value in the field was not found to be valid, see if the value was autofilled (which, by definition,
      // would mean that the value was used before and, thus, had a valid pattern)
      if (!isFieldValueValid) {
        isFieldValueValid = isFirstNameAutoFilled;
      }

      return isFieldValueValid;
    },
    [currentFirstName, isFirstNameAutoFilled]
  );

  // determines whether the last name field value is valid
  const validLastNameFieldValue: () => boolean = useCallback(
    () => {
      let isFieldValueValid: boolean = false;

      // see if the explicit value in the field (via a backing state variable) is valid
      if (currentLastName !== undefined) {
        isFieldValueValid = currentLastName.trim().length > 0;
      }

      // if the explicit value in the field was not found to be valid, see if the value was autofilled (which, by definition,
      // would mean that the value was used before and, thus, had a valid pattern)
      if (!isFieldValueValid) {
        isFieldValueValid = isLastNameAutoFilled;
      }

      return isFieldValueValid;
    },
    [currentLastName, isLastNameAutoFilled]
  );

  // determines whether the password field value is valid
  const validPasswordFieldValue: () => boolean = useCallback(
    () => {
      let isFieldValueValid: boolean = false;

      // see if the explicit value in the field (via a backing state variable) is valid
      if (currentPassword !== undefined) {
        isFieldValueValid = RegularExpressions.Password.test(currentPassword);
      }

      // if the explicit value in the field was not found to be valid, see if the value was autofilled (which, by definition,
      // would mean that the value was used before and, thus, had a valid pattern)
      if (!isFieldValueValid) {
        isFieldValueValid = isPasswordAutoFilled;
      }

      return isFieldValueValid;
    },
    [currentPassword, isPasswordAutoFilled]
  );

  // determines whether the confirm password field value is valid
  const validConfirmPasswordFieldValue: () => boolean = useCallback(
    () => {
      let isFieldValueValid: boolean = false;

      // see if the explicit value in the field (via a backing state variable) is valid
      if (currentConfirmPassword !== undefined) {
        isFieldValueValid = RegularExpressions.Password.test(currentConfirmPassword) && (currentConfirmPassword === currentPassword);
      }

      // if the explicit value in the field was not found to be valid, see if the value was autofilled (which, by definition,
      // would mean that the value was used before and, thus, had a valid pattern)
      if (!isFieldValueValid) {
        isFieldValueValid = isConfirmPasswordAutoFilled;
      }

      return isFieldValueValid;
    },
    [currentConfirmPassword, isConfirmPasswordAutoFilled]
  );

  // determines whether all required fields have a valid value (explicitly entered or browser autofilled)
  const allRequiredFieldValuesValid: () => boolean = useCallback(
    () => {
      return validEmailAddressFieldValue() && validMobilePhoneFieldValue() && validFirstNameFieldValue() && validLastNameFieldValue() && validPasswordFieldValue() && validConfirmPasswordFieldValue();
    },
    [validEmailAddressFieldValue, validMobilePhoneFieldValue, validFirstNameFieldValue, validLastNameFieldValue, validPasswordFieldValue, validConfirmPasswordFieldValue]
  );

  // to determine whether the email address field has been autofilled by the browser
  const onEmailAddressAnimationStart = useCallback(
    () =>
      (event: AnimationEvent<HTMLDivElement>): void => {
        displayConsoleLogs && console.log(`%c RegistrationForm. In onEmailAddressAnimationStart()`, 'background: #055; color: #fff');

        if (event.animationName === 'mui-auto-fill') {
          displayConsoleLogs && console.log(`%c RegistrationForm. In onEmailAddressAnimationStart(). event.animationName === 'mui-auto-fill'`, 'background: #055; color: #fff');

          if (!isEmailAddressAutoFilled) {
            setIsEmailAddressAutoFilled(true);
          }

          displayConsoleLogs && console.log(`%c RegistrationForm. In onEmailAddressAnimationStart(); getValues('email'): ${getValues('email')}`, 'background: #055; color: #fff');
        }
      },
    []
  );

  // to determine whether the mobile number field has been autofilled by the browser
  const onMobilePhoneAnimationStart = useCallback(
    () =>
      (event: AnimationEvent<HTMLDivElement>): void => {
        displayConsoleLogs && console.log(`%c RegistrationForm. In onMobilePhoneAnimationStart()`, 'background: #055; color: #fff');

        if (event.animationName === 'mui-auto-fill') {
          displayConsoleLogs && console.log(`%c RegistrationForm. In onMobilePhoneAnimationStart(). event.animationName === 'mui-auto-fill'`, 'background: #055; color: #fff');

          if (!isMobilePhoneAutoFilled) {
            setIsMobilePhoneAutoFilled(true);
          }

          displayConsoleLogs && console.log(`%c RegistrationForm. In onMobilePhoneAnimationStart(); getValues('mobilePhone'): ${getValues('mobilePhone')}`, 'background: #055; color: #fff');
        }
      },
    []
  );

  // to determine whether the first name field has been autofilled by the browser
  const onFirstNameAnimationStart = useCallback(
    () =>
      (event: AnimationEvent<HTMLDivElement>): void => {
        displayConsoleLogs && console.log(`%c RegistrationForm. In onFirstNameAnimationStart()`, 'background: #055; color: #fff');

        if (event.animationName === 'mui-auto-fill') {
          displayConsoleLogs && console.log(`%c RegistrationForm. In onFirstNameAnimationStart(). event.animationName === 'mui-auto-fill'`, 'background: #055; color: #fff');

          if (!isFirstNameAutoFilled) {
            setIsFirstNameAutoFilled(true);
          }

          displayConsoleLogs && console.log(`%c RegistrationForm. In onFirstNameAnimationStart(); getValues('firstName'): ${getValues('firstName')}`, 'background: #055; color: #fff');
        }
      },
    []
  );

  // to determine whether the last name field has been autofilled by the browser
  const onLastNameAnimationStart = useCallback(
    () =>
      (event: AnimationEvent<HTMLDivElement>): void => {
        displayConsoleLogs && console.log(`%c RegistrationForm. In onLastNameAnimationStart()`, 'background: #055; color: #fff');

        if (event.animationName === 'mui-auto-fill') {
          displayConsoleLogs && console.log(`%c RegistrationForm. In onLastNameAnimationStart(). event.animationName === 'mui-auto-fill'`, 'background: #055; color: #fff');

          if (!isLastNameAutoFilled) {
            setIsLastNameAutoFilled(true);
          }

          displayConsoleLogs && console.log(`%c RegistrationForm. In onLastNameAnimationStart(); getValues('lastName'): ${getValues('lastName')}`, 'background: #055; color: #fff');
        }
      },
    []
  );

  // to determine whether the password field has been autofilled by the browser
  const onPasswordAnimationStart = useCallback(
    () =>
      (event: AnimationEvent<HTMLDivElement>): void => {
        displayConsoleLogs && console.log(`%c RegistrationForm. In onPasswordAnimationStart()`, 'background: #055; color: #fff');

        if (event.animationName === 'mui-auto-fill') {
          displayConsoleLogs && console.log(`%c RegistrationForm. In onPasswordAnimationStart(). event.animationName === 'mui-auto-fill'`, 'background: #055; color: #fff');

          if (!isPasswordAutoFilled) {
            setIsPasswordAutoFilled(true);
          }

          displayConsoleLogs && console.log(`%c RegistrationForm. In onPasswordAnimationStart(); getValues('password'): ${getValues('password')}`, 'background: #055; color: #fff');
        }
      },
    []
  );

  // to determine whether the confirm password field has been autofilled by the browser
  const onConfirmPasswordAnimationStart = useCallback(
    () =>
      (event: AnimationEvent<HTMLDivElement>): void => {
        displayConsoleLogs && console.log(`%c RegistrationForm. In onConfirmPasswordAnimationStart()`, 'background: #055; color: #fff');

        if (event.animationName === 'mui-auto-fill') {
          displayConsoleLogs && console.log(`%c RegistrationForm. In onConfirmPasswordAnimationStart(). event.animationName === 'mui-auto-fill'`, 'background: #055; color: #fff');

          if (!isConfirmPasswordAutoFilled) {
            setIsConfirmPasswordAutoFilled(true);
          }

          displayConsoleLogs && console.log(`%c RegistrationForm. In onConfirmPasswordAnimationStart(); getValues('password'): ${getValues('confirmPassword')}`, 'background: #055; color: #fff');
        }
      },
    []
  );

  useEffect(() => {
    // check for either values or autofilled status for each of the prospectivly autofilled fields
    setFormIsValid(isValid || (isEmailAddressAutoFilled && isPasswordAutoFilled));
  }, [isValid, isEmailAddressAutoFilled, isMobilePhoneAutoFilled, isPasswordAutoFilled, isConfirmPasswordAutoFilled, isFirstNameAutoFilled, isLastNameAutoFilled]);

  // useEffect to be executed upon mounting of this component
  useEffect(() => {
    // prepare an array of Theme values from the enumTheme enumerator that can be used to populate MenuItem components for the Theme <Select> component
    let themeMenuItems: Array<React.JSX.Element> = [];
    KeyValuePairsStringAssets.themeKeyValuePairs.forEach((keyValuePair: { key: string, value: string }) => {
      themeMenuItems.push(<MenuItem key={keyValuePair.key} value={keyValuePair.key}>{keyValuePair.value}</MenuItem>);
    });

    setThemesForDropdown(themeMenuItems);
    setThemeKey(enumTheme.Default);

    // prepare an array of Locale values from the enumLocale enumerator that can be used to populate MenuItem components for the PreferredLocale <Select> component
    let localeMenuItems: Array<React.JSX.Element> = [];
    KeyValuePairsStringAssets.localeKeyValuePairs.forEach((keyValuePair: { key: string, value: string }) => {
      localeMenuItems.push(<MenuItem key={keyValuePair.key} value={keyValuePair.key}>{keyValuePair.value}</MenuItem>);
    });

    setLocalesForDropdown(localeMenuItems);
    setPreferredLocaleKey(enumLocale.English_US);

    // prepare an array of SubscriptionType values from the enumSubscriptionType enumerator that can be used to populate MenuItem components for the SubscriptionType <Select> component
    let membershipTypeMenuItems: Array<React.JSX.Element> = [];
    KeyValuePairsStringAssets.subscriptionTypeValuePairs.forEach((keyValuePair: { key: string, value: string }) => {
      membershipTypeMenuItems.push(<MenuItem key={keyValuePair.key} value={keyValuePair.key}>{keyValuePair.value}</MenuItem>);
    });

    setMembershipTypesForDropdown(membershipTypeMenuItems);
  }, []);

  useEffect(() => {
    if ((membershipTypesForDropdown.length > 0)  ) {
      setSubscriptionTypeKey(enumSubscriptionTypeConvert.toString(enumSubscriptionType.BetaTesting));
    }
  }, [membershipTypesForDropdown]);

  // useEffect to establish whether the form contains valid data such that the user will be allowed to proceed
  useEffect(() => {
    // determine whether the form is valid (has met all requirements) based on the 'isValid' flag (a reflection of the react-hook-form state) and whether the registration code requirements have been met.
    const isFormValid: boolean = isValid && registrationCodeIsValid;

    setFormIsValid(isFormValid);
    // console.info(`RegistrationForm.useEffect: formIsValid being set to ${isFormValid}`);

    // // if the registration code is NOT valid, set an error for this field
    // if (!registrationCodeIsValid) {
    //   setError('registrationCode', { type: 'custom', message: ControlsStringAssets.registrationCodeInvalid });
    // } else {
    //   // the registration code IS valid, so clear the errors
    //   clearErrors('registrationCode');
    // }

  }, [isValid, registrationCodeIsValid]);

  // useEffect to determine whether the registration code is valid
  useEffect(() => {
    displayConsoleLogs && console.log(`RegistrationForm. useEffect for [registrationCodes, registrationCodeFieldValue]. registrationCodes: ${JSON.stringify(registrationCodes)}; registrationCodeFieldValue: ${registrationCodeFieldValue}.`);

    // see if the registration code field value (trimmed of leading and trailing spaces) matches one of the legitimate registration codes
    const isRegistrationCodeValid: boolean = registrationCodes.includes(registrationCodeFieldValue.trim());

    // set the flag to indicate whether the registration code is valid
    setRegistrationCodeIsValid(isRegistrationCodeValid);

    // // if the registration code is NOT valid, set an error for this field
    // if (!isRegistrationCodeValid) {
    //   setError('registrationCode', { type: 'custom', message: ControlsStringAssets.registrationCodeInvalid });
    // } else {
    //   // the registration code IS valid, so clear the errors
    //   clearErrors('registrationCode');
    // }

  }, [registrationCodes, registrationCodeFieldValue]);

  // useEffect for changes to the currentEmailAddress. If it's a valid email address, we'll get a snapshot of userRedemptionCodes associated
  // with the email address
  useEffect(() => {
    // if the email address value is a valid email address...
    if (isEmail(currentEmailAddress)) {

      getUserRedemptionCodeRequestsForEmailAddressViaHttp(currentEmailAddress).then((userRedemptionCodeRequests: Array<IUserRedemptionCodeRequestAsJson>) => {
        displayConsoleLogs && console.log(`RegistrationForm. useEffect for currentEmailAddress. Results of getUserRedemptionCodeRequestsForEmailAddressViaHttp: ${JSON.stringify(userRedemptionCodeRequests)}.`);

        // for now, if there are any redemption code requests returned, ...
        if (userRedemptionCodeRequests.length > 0) {

          // Because the call to get the user redemption code from the server is asynchronous and because the user could be typing somewhat rapidly, it's possible that the return value doesn't match the email 
          // address sent with the request. Therefore, ensure that the current email address matches the email address in the structure returned from the request.
          if (currentEmailAddress.toLowerCase().trim() === userRedemptionCodeRequests[0].recipientEmailAddress?.toLowerCase().trim()) {
            // get the first code and set its value in the Redemption (Registration) Code field
            const registrationCodeReturned: string = userRedemptionCodeRequests[0].redemptionCode ?? 'unknown';
            setRegistrationCodeFieldValue(registrationCodeReturned);
            setValue('registrationCode', registrationCodeReturned);

            // Capture an array of registrationCodes (redemptionCodes) found for the user. Only capture defined values.
            const registrationCodesForUser: Array<string> = [];
            for (let idx = 0; idx < userRedemptionCodeRequests.length; idx++) {
              const redemptionCode: string | undefined = userRedemptionCodeRequests[idx].redemptionCode;
              if (redemptionCode !== undefined) {
                registrationCodesForUser.push(redemptionCode);
              }
            }
            setRegistrationCodes(registrationCodesForUser);
          }
        } else {
          // clear out the value of the 'registrationCode' (redemptionCode) field
          setValue('registrationCode', '');

          // set other registration/redemption code state variables to empty arrays
          setRegistrationCodes([]);
          setUserRedemptionCodeRequests([]);
        }
      });

    } else {
      // clear out the value of the 'registrationCode' (redemptionCode) field
      setValue('registrationCode', '');

      // set other registration/redemption code state variables to empty arrays
      setRegistrationCodes([]);
      setUserRedemptionCodeRequests([]);
    }
  }, [currentEmailAddress])


  // capture whether a registration is currently being submitted
  const registrationRequestInProgress: boolean = props.registrationRequestInProgress ?? false;

  // state value indicating whether a registration is in progress
  const [registrationInProgress, setRegistrationInProgress] = useState<boolean>(registrationRequestInProgress);

  // useEffect hook for setting the 'registrationInProgress' local state based on whether a registration is currently in progress
  useEffect(() => {
    setRegistrationInProgress(registrationRequestInProgress);
  }, [registrationRequestInProgress]);


  // handles a registration (submit) request from the form
  const handleRegistrationSubmit = async (data: IRegistrationFormValues) => {

    setRegistrationInProgress(true);

    // fill in all fields within the registrationData object passed in
    registrationData.email = data.email.toLowerCase();
    registrationData.password = data.password;
    registrationData.firstName = data.firstName;
    registrationData.lastName = data.lastName;
    registrationData.mobilePhone = data.mobilePhone;
    registrationData.theme = data.theme;
    registrationData.preferredLocale = data.preferredLocale;
    registrationData.subscriptionType = enumSubscriptionTypeConvert.fromString(data.membershipType);
    registrationData.subscriptionStartDate = new Date();
    registrationData.subscriptionEndDate = undefined;
    registrationData.registrationCode = data.registrationCode;

    // call the onSubmit handler passed in, supplying the Channel object
    await onSubmit(registrationData);
  }


  // // An 'onChange' handler for the Email field
  // function handleEmailChange(emailAddressFieldValue: string): void {
  //   displayConsoleLogs && console.log(`%c RegistrationForm.handleEmailChange. emailAddressFieldValue: ${emailAddressFieldValue}`, 'background: #055; color: #fff');

  //   // if (isEmail(emailAddressFieldValue)) {
  //   //   if (currentEmailAddress !== emailAddressFieldValue) {
  //   //     setCurrentEmailAddress(emailAddressFieldValue);
  //   //   }
  //   // }

  //   // // if it's a valid email address, trigger request to get a snapshot of all userRedemptionCodes associated with the email address
  //   // if (isEmail(emailAddressFieldValue)) {

  //   // } else {
  //   //   // clear the userRedemptionCode (registrationCode) field
  //   // }

  //   // set the current value of the email address field
  //   setCurrentEmailAddress(emailAddressFieldValue.toLowerCase());
  // }

  // An 'onChange' handler for the RegistrationCode field
  function handleRegistrationCodeChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setRegistrationCodeFieldValue(event.target.value); // capture changed value of registrationCode
  }


  // prepare an array of Locale values from the enumLocale enumerator that can be used to populate MenuItem components for the Preferred Locale <Select> component
  let localeMenuItems: Array<React.JSX.Element> = [];
  Object.values(enumLocale).forEach((localeValue: string) => {
    localeMenuItems.push(<MenuItem key={localeValue} value={localeValue}>{localeValue}</MenuItem>);
  });

  // prepare an array of Theme values from the enumTheme enumerator that can be used to populate MenuItem components for the Theme <Select> component
  let themeMenuItems: Array<React.JSX.Element> = [];
  Object.values(enumTheme).forEach((themeValue: string) => {
    themeMenuItems.push(<MenuItem key={themeValue} value={themeValue}>{themeValue}</MenuItem>);
  });

  return (

    <Form
      onSubmit={handleSubmit(handleRegistrationSubmit)}
    >
      <Grid container >
        <Grid container key='emailAndPhone' size={12} spacing={1} justifyContent='space-between' >
          <Grid key='email' size={{ xs: 12, sm: 6 }} >
            <TextField
              inputRef={emailReg}
              {...emailProps}
              autoFocus
              label={ControlsStringAssets.emailLabel}
              margin='normal'
              fullWidth
              error={!!errors.email}
              helperText={errors?.email?.message}
              slotProps={{
                inputLabel: {
                  required: true // this will cause an asterisk ('*') to appear at the end of the label text
                },
                input: {
                  onAnimationStart: onEmailAddressAnimationStart()
                }
              }}
              onChange={e => setCurrentEmailAddress(e.target.value.toLowerCase())}
            />
          </Grid>
          <Grid key='mobilePhone' size={{ xs: 12, sm: 6 }} >
            <TextField
              type="tel"
              inputRef={mobilePhoneReg}
              {...mobilePhoneProps}
              label={ControlsStringAssets.mobilePhoneLabel}
              margin='normal'
              fullWidth
              error={!!errors.mobilePhone}
              helperText={errors?.mobilePhone?.message}
              slotProps={{
                inputLabel: {
                  required: true // this will cause an asterisk ('*') to appear at the end of the label text
                },
                input: {
                  onAnimationStart: onMobilePhoneAnimationStart()
                }
              }}
              // we need a custom onChange handler to normalize (format) the phone number as the user is entering the digits
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                e.currentTarget.value = normalizePhoneNumber(e.currentTarget.value);
                setCurrentMobilePhone(normalizePhoneNumber(e.currentTarget.value));
              }}
            />
          </Grid>
        </Grid>
        <Grid container key='passwordAndConfirmPassword' size={12} spacing={1} justifyContent='space-between' >
          <Grid key='password' size={{ xs: 12, sm: 6 }} >
            <TextField
              inputRef={passwordReg}
              {...passwordProps}
              type="password"
              label={ControlsStringAssets.passwordLabel}
              margin='normal'
              fullWidth
              error={!!errors.password}
              helperText={errors?.password?.message}
              slotProps={{
                inputLabel: {
                  required: true // this will cause an asterisk ('*') to appear at the end of the label text
                },
                input: {
                  onAnimationStart: onPasswordAnimationStart()
                }
              }}
              onChange={e => setCurrentPassword(e.target.value)}
            />
          </Grid>
          <Grid key='confirmPassword' size={{ xs: 12, sm: 6 }} >
            <TextField
              inputRef={confirmPasswordReg}
              {...confirmPasswordProps}
              type="password"
              label={ControlsStringAssets.confirmPasswordLabel}
              margin='normal'
              fullWidth
              error={!!errors.confirmPassword}
              helperText={errors?.confirmPassword?.message}
              slotProps={{
                inputLabel: {
                  required: true // this will cause an asterisk ('*') to appear at the end of the label text
                },
                input: {
                  onAnimationStart: onConfirmPasswordAnimationStart()
                }
              }}
              onChange={e => setCurrentConfirmPassword(e.target.value)}
            />
          </Grid>
        </Grid>
        <Grid container key='firstNameAndLastName' size={12} spacing={1} justifyContent='space-between' >
          <Grid key='firstName' size={{ xs: 12, sm: 6 }} >
            <TextField
              inputRef={firstNameReg}
              {...firstNameProps}
              label="First Name"
              margin='normal'
              fullWidth
              error={!!errors.firstName}
              helperText={errors?.firstName?.message}
              slotProps={{
                inputLabel: {
                  required: true // this will cause an asterisk ('*') to appear at the end of the label text
                },
                input: {
                  onAnimationStart: onFirstNameAnimationStart()
                }
              }}
              onChange={e => setCurrentFirstName(e.target.value)}
            />
          </Grid>
          <Grid key='lastName' size={{ xs: 12, sm: 6 }} >
            <TextField
              inputRef={lastNameReg}
              {...lastNameProps}
              label="Last Name"
              margin='normal'
              fullWidth
              error={!!errors.lastName}
              helperText={errors?.lastName?.message}
              slotProps={{
                inputLabel: {
                  required: true // this will cause an asterisk ('*') to appear at the end of the label text
                },
                input: {
                  onAnimationStart: onLastNameAnimationStart()
                }
              }}
              onChange={e => setCurrentLastName(e.target.value)}
            />
          </Grid>
        </Grid>
        {/* For now, we are going to completely hide (via 'display: none') the next row in the grid. Default values will be used for Theme & Language*/}
        <Grid style={{ display: 'none' }} container key='themeAndLocale' size={12} spacing={1} justifyContent='space-between' >
          <Grid key='theme' size={{ xs: 12, sm: 6 }} >

            {/* We use a TextField with 'select' attribute as a pseudo <Select> (or dropdown) control */}
            <StyledTextFieldForSelectControl
              sx={{ visibility: 'hidden' }}  // This Theme field will be hidden for now. The user will be able to change theme in 'Settings'.
              inputRef={themeReg}
              label="Theme"
              margin='normal'
              value={themeKey}
              onChange={e => setThemeKey(e.target.value)}
            >
              {themesForDropdown}
            </StyledTextFieldForSelectControl>

          </Grid>
          <Grid key='locale' size={{ xs: 12, sm: 6 }} >
            {/* We use a TextField with 'select' attribute as a pseudo <Select> (or dropdown) control */}
            <StyledTextFieldForSelectControl
              sx={{ visibility: 'hidden' }}  // This Locale field will be hidden for now, until we are ready to enable internationalization features
              inputRef={preferredLocaleReg}
              label="Preferred Locale"
              margin='normal'
              value={preferredLocaleKey}
              onChange={e => setPreferredLocaleKey(e.target.value)}
            >
              {localesForDropdown}
            </StyledTextFieldForSelectControl>
          </Grid>
        </Grid>
        <Grid container key='membershipTypeAndRegistrationCode' size={12} spacing={1} justifyContent='space-between' >
          <Grid key='locale' size={{ xs: 12, sm: 6 }} >
            {/* We use a TextField with 'select' attribute as a pseudo <Select> (or dropdown) control */}
            <StyledTextFieldForSelectControl
              // sx={{ visibility: 'hidden' }}  // This Membership Type field will be hidden for now, until we are ready to enable Membership features
              inputRef={membershipTypeReg}
              label={ControlsStringAssets.membershipTypeLabel}
              margin='normal'
              value={membershipTypeKey}
              onChange={e => setSubscriptionTypeKey(e.target.value)}
            >
              {membershipTypesForDropdown}
            </StyledTextFieldForSelectControl>
          </Grid>
          <Grid key='registrationCode' size={{ xs: 12, sm: 6 }} >
            <TextField
              inputRef={registrationCodeReg}
              {...registrationCodeProps}
              label="Registration Code"
              margin='normal'
              fullWidth
              error={!!errors.registrationCode}
              helperText={errors?.registrationCode?.message || (!registrationCodeIsValid && ControlsStringAssets.registrationCodeInvalid)}
              slotProps={{
                inputLabel: {
                  required: true, // this will cause an asterisk ('*') to appear at the end of the label text
                  shrink: true // this will force the label to automatically shrink for the field
                }
              }}

              onChange={event => handleRegistrationCodeChange(event)} // 'Change' handler for the field
            />
          </Grid>
        </Grid>
        <Grid container key='agreeToTermsAnd???' size={12} >
          <Grid key='agreeToTerms' size={12} >
            {/* We use a FormControlLabel to bundle a Material-UI Checkbox with a label */}
            <FormControlLabel
              name="agreeToTerms"
              control={
                <Checkbox
                  name="agreeToTerms"
                  required
                  color='primary'
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setAgreeToTerms(event.currentTarget.checked);
                  }}
                />
              }
              label={ControlsStringAssets.termsAndConditionsLabel}
            />
          </Grid>
        </Grid>
      </Grid>

      {/* if there's a registration in progress, display the progress bar area */}
      {registrationInProgress &&
        <StyledBoxForProgressBarArea>
          <StyledLinearProgressForProgressBar />
          <StyledBoxForProgressLabelArea>
            {/* if an action progress label has been provided, display it */}
            <Typography variant="caption">{MessagesStringAssets.registration_Requested}</Typography>
          </StyledBoxForProgressLabelArea>
        </StyledBoxForProgressBarArea>
      }
      {/* display the action buttons ("Create New Account" & "Login") */}

      <StyledBoxForActionButtonsArea>
        <SubmitButton
          fullWidth={true}
          disabled={!formIsValid || !agreeToTerms || registrationInProgress}
        >
          {ControlsStringAssets.createNewAccountButtonText}
        </SubmitButton>

        <StyledBoxForPseudoHorizontalRule>
          <StyledBoxForTextInPseudoHorizontalRule>OR</StyledBoxForTextInPseudoHorizontalRule>
        </StyledBoxForPseudoHorizontalRule>

        <StyledButtonForLoginButton
          disabled={registrationInProgress}
          onClick={() => navigate('/signin')}
        >
          {ControlsStringAssets.loginButtonText}
        </StyledButtonForLoginButton>
      </StyledBoxForActionButtonsArea>

    </Form>

  );
}

export default RegistrationForm;